import type { ComponentProps } from 'react';
import { useCallback, useRef } from 'react';

import type { DialogsContainerProps } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import type { LoadableType } from '@confluence/loadable';
import { LoadableAfterPaint } from '@confluence/loadable';
import type { FeedbackType } from '@atlassian/company-hub-common';

type FeedbackCollectorLoaderProps = ComponentProps<typeof FeedbackCollectorLoader>;

export function useFeedbackCollector(
	props: Omit<FeedbackCollectorLoaderProps, keyof DialogsContainerProps>,
): () => void {
	const { showModal } = useDialogs();

	const propsRef = useRef<typeof props>();
	propsRef.current = props;

	return useCallback(() => showModal(FeedbackCollectorLoader, propsRef.current!), [showModal]);
}

const FeedbackCollectorLoader: LoadableType<
	Omit<
		DialogsContainerProps & {
			source: string;
			type: FeedbackType;
		},
		'flags'
	>
> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-LazyFeedbackCollector" */ './FeedbackCollector.tsx'
			)
		).FeedbackCollector,
});
